import { css } from '@emotion/react';
import {
  faSignsPost, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  AccordionComponent, AccordionHeaderSize,
} from '~/_shared/baseComponents/accordion';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { EmptyStateComponent } from '~/_shared/components/emptyState/emptyState.component';
import { ExportFileButtonContainer } from '~/_shared/components/exportFileButton/exportFileButton.container';
import { HoverableIconComponent } from '~/_shared/components/icons/hoverableIcon.component';
import { PrintFileButtonComponent } from '~/_shared/components/printFileButton/printFileButton.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type ThemeProps } from '~/_shared/types/themeProps';
import { useTranslation } from '~/_shared/utils/hooks';
import { MapToolWrapperComponent } from '~/sidebar/sidebarApps/mapToolWrapper/mapToolWrapper.component';
import { useDirectionsExpandedRouteIdsSelector } from '~/store/frontendState/mapTools/directions/directions.selectors';
import { type MapSettingsRoute } from '~/store/mapSettings/directions/mapSettingsDirections.state';
import { DirectionsListingContainer } from '../listing/directionsListing.container';
import { DirectionsPanelFormContainer } from './form/directionsPanelForm.container';

const expandedSubItemStyle = ({ theme, hideBorder }: ThemeProps<{hideBorder: boolean}>) => css({
  borderBottom: `1px solid${hideBorder ? theme.accordionColors.borderColor : theme.accordionColors.headingBorderActive}`, //To prevent a double border (header bottom and first child top when active)
});

const emptyStateWrapperStyle = css({
  padding: '50px 30px 16px',
});

const bodyRootStyle = css({
  padding: 0,
});

const headerStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

type DirectionsPanelProps = Readonly<{
  routes: ReadonlyArray<MapSettingsRoute>;
  isLegendMode: boolean;

  deleteAllRoutes: () => void;
  exportFile?: () => void;
}>;

export const DirectionsPanelComponent: FC<DirectionsPanelProps> = (props) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const expandedRouteIds = useDirectionsExpandedRouteIdsSelector();

  const [listingPrintVersionElement, setListingPrintVersionElement] = useState<HTMLDivElement | null>(null);
  const [isPrintingMode, setPrintingMode] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: { current: listingPrintVersionElement },
    onAfterPrint: () => {
      setPrintingMode(false);
    },
  });
  const firstRouteId = props.routes[0]?.id;

  const listingPrintVersionRef = useCallback((node: HTMLDivElement | null) => {
    setListingPrintVersionElement(node);
  }, []);

  useEffect(() => {
    if (listingPrintVersionElement && isPrintingMode) {
      handlePrint();
    }
  }, [handlePrint, listingPrintVersionElement, isPrintingMode]);

  return (
    <MapToolWrapperComponent
      footer={props.routes.length !== 0 && (
        <>
          <PrintFileButtonComponent
            onPrintClick={() => setPrintingMode(true)}
          />
          {(props.exportFile && (
            <ExportFileButtonContainer
              onExportFileClick={props.exportFile}
            />
          ))}
        </>
      )}
      isLoading={isPrintingMode}
    >
      {!props.isLegendMode && (
        <DirectionsPanelFormContainer
          routes={props.routes}
        />
      )}

      <AccordionComponent
        data={[{
          child: props.routes.length ? (
            <>
              <DirectionsListingContainer />
              {isPrintingMode && (
                <DirectionsListingContainer
                  ref={listingPrintVersionRef}
                  isPrintVersion
                />
              )}
            </>
          ) : (
            <EmptyStateComponent
              css={emptyStateWrapperStyle}
              icon={faSignsPost}
              title={t('directionsTool.emptyStateTitle')}
              description={t('directionsTool.emptyStateDescription')}
            />
          ),
          header: (
            <div css={headerStyle}>
              <span>{t('directionsTool.listing.header').toUpperCase()}</span>
              <TooltipComponent
                tooltipContent={t('Delete all')}
              >
                <HoverableIconComponent
                  icon={faTrash}
                  hoverColor={theme.iconColors.danger}
                  title={t('Delete all')}
                  onClick={props.routes.length === 0 ? undefined : props.deleteAllRoutes}
                  isDisabled={props.routes.length === 0}
                />
              </TooltipComponent>
            </div>
          ),
          isExpanded: true,
          isLocked: true,
        }]}
        expandedItemStyle={expandedSubItemStyle({ theme, hideBorder: !!(firstRouteId && expandedRouteIds.has(firstRouteId)) })}
        headerSize={AccordionHeaderSize.Large}
        panelStyle={bodyRootStyle}
      />
    </MapToolWrapperComponent>
  );
};
